import { ListItemIcon, ListItemText, MenuItem, Stack } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission, AssetDto, PoolItemEntityType } from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import AppMenuItem from "../../Menu/AppMenuItem";
import HeaderMenuItem from "../../Menu/HeaderMenuItem";
import PoolItemCreateFromEntityModal from "../PoolItem/PoolItemCreateFromEntityModal";
import AssetDeleteModal from "./AssetDeleteModal";

enum ActionFlags {
  Edit = "Edit",
  Delete = "Delete",
  AddToPool = "AddToPool",
}
const defaultDisplayProps = {
  actions: {
    edit: true,
    delete: true,
    addToPool: true,
  },
};

interface TriggersProps {
  entity?: AssetDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity?: AssetDto | null;
  disabled?: boolean;
  onUpdate: ((newValue: AssetDto | null | undefined) => void) | undefined;
  onDelete?: (() => void) | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  customActions?: (props: TriggersProps) => ReactNode;
}

export default function AssetMenu({
  entity,
  disabled,
  customActions,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}
          {displayProps?.actions?.edit && (
            <AuthorizedMenuItem
              permissions={[AppPermission.AssetManage]}
              component={RouterLink}
              to={ROUTE_PATH.ASSET_EDIT(entity?.id)}
            >
              <ListItemIcon>
                <AppIcon of='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </AuthorizedMenuItem>
          )}
          {displayProps?.actions?.delete && (
            <AppMenuItem
              permissions={[AppPermission.AssetManage]}
              onClick={() => startAction(ActionFlags.Delete)}
            >
              <ListItemIcon>
                <AppIcon of='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </AppMenuItem>
          )}
          {displayProps?.actions?.addToPool && (
            <>
              <HeaderMenuItem primaryTitle='Quick actions' />
              <AuthorizedElement permissions={[AppPermission.PoolManage]}>
                <MenuItem onClick={() => startAction(ActionFlags.AddToPool)}>
                  <ListItemIcon>
                    <AppIcon of='pool' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Add to pool</ListItemText>
                </MenuItem>
              </AuthorizedElement>
            </>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Delete */}
          {entity && (
            <AssetDeleteModal
              entity={entity}
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              onDelete={() => {
                onDelete && onDelete();
                completeAction();
              }}
            />
          )}

          {/* Add to pool */}
          {entity && (
            <PoolItemCreateFromEntityModal
              open={currentAction === ActionFlags.AddToPool}
              onClose={() => cancelAction()}
              createProps={{
                entityType: PoolItemEntityType.Asset,
                entity: entity,
                onSave: (newValue) => {
                  onUpdate && onUpdate(null);
                  completeAction();
                },
              }}
            />
          )}
        </Stack>
      )}
    />
  );
}
